import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Button,
    Grid,
    Breadcrumbs,
    Container,
    Tooltip
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ExpandMore, ChevronRight, Download, InfoOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { reportAPI } from '../../services/reportAPI';
import { formatDateForInput } from '../../utils/dateUtils';

interface RevenueBreakdownItem {
    category: string;
    subcategory?: string;
    amount: number;
    tax: number;
    totalAmount: number;
    refunds: number;
    outstanding: number;
    isSubcategory?: boolean;
    level?: number;
}

export const AccountingReport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [revenueBreakdown, setRevenueBreakdown] = useState<RevenueBreakdownItem[]>([]);
    
    // Initialize date range to current month
    const now = new Date();
    const [startDate, setStartDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));
    const [endDate, setEndDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    const [arCollected, setArCollected] = useState<number>(0);
    const [cancelledTransactions, setCancelledTransactions] = useState<number>(0);
    
    // Track expanded categories for collapsible subcategories
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['Membership Payments']));

    useEffect(() => {
        loadReportData();
    }, [startDate, endDate]);

    const loadReportData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const startDateStr = formatDateForInput(startDate);
            const endDateStr = formatDateForInput(endDate);

            console.log('[AccountingReport] Fetching data for:', startDateStr, 'to', endDateStr);

            // Fetch transaction stats for selected period
            const stats = await reportAPI.getTransactionStats({
                startDate: startDateStr,
                endDate: endDateStr
            });

            console.log('[AccountingReport] Revenue breakdown:', stats.revenueBreakdown);

            setRevenueBreakdown(stats.revenueBreakdown || []);
            setArCollected(stats.arCollected || 0);
            setCancelledTransactions(stats.cancelledTransactions || 0);
        } catch (err: any) {
            console.error('Error loading accounting report:', err);
            setError(err.message || 'Failed to load report data');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    const handleExportCSV = () => {
        if (!revenueBreakdown || revenueBreakdown.length === 0) return;

        // Get visible rows (respecting expanded/collapsed state)
        const visibleRows = revenueBreakdown.filter(item => {
            if (!item.isSubcategory) return true;
            return expandedCategories.has(item.category);
        });

        // Create CSV content
        const headers = ['Category', 'Credit', 'Debit', 'Tax', 'Outstanding', 'Net'];
        const csvRows = visibleRows.map(item => [
            item.subcategory || item.category,
            (item.amount + Math.abs(item.refunds) + item.tax).toFixed(2),
            item.refunds.toFixed(2),
            item.tax.toFixed(2),
            item.outstanding.toFixed(2),
            (item.amount + item.tax).toFixed(2)
        ]);

        // Add AR Collected row if present
        if (arCollected > 0) {
            csvRows.push([
                'Accounts Receivable Collected',
                '0.00',
                '0.00',
                '0.00',
                '0.00',
                arCollected.toFixed(2)
            ]);
        }

        // Add Cancelled Transactions row if present
        if (cancelledTransactions > 0) {
            csvRows.push([
                'Cancelled Transactions',
                '0.00',
                '0.00',
                '0.00',
                '0.00',
                `-${cancelledTransactions.toFixed(2)}`
            ]);
        }

        const csvContent = [
            headers.join(','),
            ...csvRows.map(row => row.join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `accounting-report-${formatDateForInput(startDate)}-to-${formatDateForInput(endDate)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const toggleCategory = (category: string) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(category)) {
                newSet.delete(category);
            } else {
                newSet.add(category);
            }
            return newSet;
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/reports"
                >
                    Reports
                </RouterLink>
                <Typography color="text.primary">Accounting Report</Typography>
            </Breadcrumbs>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Accounting Report
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Date Range Selector */}
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(newValue) => newValue && setStartDate(newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small'
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(newValue) => newValue && setEndDate(newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small'
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Revenue Summary Table */}
                <Paper elevation={2} sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            Revenue Summary
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Download />}
                            onClick={handleExportCSV}
                            disabled={!revenueBreakdown || revenueBreakdown.length === 0}
                        >
                            Export CSV
                        </Button>
                    </Box>
                    {revenueBreakdown && revenueBreakdown.length > 0 ? (
                        <DataGrid
                            rows={
                                (() => {
                                    const dataRows = revenueBreakdown
                                        .filter(item => {
                                            const level = item.level ?? (item.isSubcategory ? 1 : 0);
                                            
                                            // Always show level 0 (parent categories)
                                            if (level === 0) return true;
                                            
                                            // For level 1 (types/tiers), show if parent is expanded
                                            if (level === 1) {
                                                return expandedCategories.has(item.category);
                                            }
                                            
                                            // For level 2 (individual items), find parent level 1 and check if expanded
                                            if (level === 2) {
                                                const parentLevel1 = revenueBreakdown.find(
                                                    parent => parent.level === 1 && 
                                                             parent.category === item.category &&
                                                             item.subcategory?.includes(parent.subcategory || '')
                                                );
                                                
                                                if (parentLevel1) {
                                                    const parentKey = `${parentLevel1.category}::${parentLevel1.subcategory}`;
                                                    return expandedCategories.has(parentKey);
                                                }
                                                return false;
                                            }
                                            
                                            return false;
                                        })
                                        .map((item, index) => {
                                            const level = item.level ?? (item.isSubcategory ? 1 : 0);
                                            
                                            // Check if this item has children
                                            let hasChildren = false;
                                            if (level === 0) {
                                                hasChildren = revenueBreakdown.some(child => child.level === 1 && child.category === item.category);
                                            } else if (level === 1) {
                                                hasChildren = revenueBreakdown.some(
                                                    child => child.level === 2 && 
                                                            child.category === item.category && 
                                                            child.subcategory?.includes(item.subcategory || '')
                                                );
                                            }
                                            
                                            return {
                                                id: index,
                                                category: item.subcategory || item.category,
                                                credit: item.amount + Math.abs(item.refunds) + item.tax,
                                                debit: item.refunds,
                                                tax: item.tax,
                                                outstanding: item.outstanding,
                                                net: item.amount + item.tax,
                                                level: level,
                                                parentCategory: item.category,
                                                parentSubcategory: item.subcategory,
                                                hasChildren: hasChildren,
                                                expandKey: level === 0 ? item.category : `${item.category}::${item.subcategory}`
                                            };
                                        });

                                    // Calculate totals from all level 0 (parent) rows only to avoid double counting
                                    const parentRows = revenueBreakdown.filter(item => {
                                        const level = item.level ?? (item.isSubcategory ? 1 : 0);
                                        return level === 0;
                                    });
                                    
                                    const totalCredit = parentRows.reduce((sum, item) => sum + item.amount + Math.abs(item.refunds) + item.tax, 0);
                                    const totalDebit = parentRows.reduce((sum, item) => sum + item.refunds, 0);
                                    const totalTax = parentRows.reduce((sum, item) => sum + item.tax, 0);
                                    const totalOutstanding = parentRows.reduce((sum, item) => sum + item.outstanding, 0);
                                    const totalNet = parentRows.reduce((sum, item) => sum + item.amount + item.tax, 0) + arCollected;

                                    // Add AR Collected row (prior-period invoices collected in this period) before TOTAL
                                    if (arCollected > 0) {
                                        dataRows.push({
                                            id: -1,
                                            category: 'Accounts Receivable Collected',
                                            credit: 0,
                                            debit: 0,
                                            tax: 0,
                                            outstanding: 0,
                                            net: arCollected,
                                            level: 0,
                                            parentCategory: '',
                                            parentSubcategory: '',
                                            hasChildren: false,
                                            expandKey: 'ar_collected'
                                        });
                                    }

                                    // Add Cancelled Transactions row (prior-period transactions cancelled in this period)
                                    if (cancelledTransactions > 0) {
                                        dataRows.push({
                                            id: -2,
                                            category: 'Cancelled Transactions',
                                            credit: 0,
                                            debit: 0,
                                            tax: 0,
                                            outstanding: 0,
                                            net: -cancelledTransactions,
                                            level: 0,
                                            parentCategory: '',
                                            parentSubcategory: '',
                                            hasChildren: false,
                                            expandKey: 'cancelled_transactions'
                                        });
                                    }

                                    return [
                                        ...dataRows,
                                        {
                                            id: 'total',
                                            category: 'TOTAL',
                                            credit: totalCredit,
                                            debit: totalDebit,
                                            tax: totalTax,
                                            outstanding: totalOutstanding,
                                            net: totalNet,
                                            level: -1,
                                            parentCategory: '',
                                            parentSubcategory: '',
                                            hasChildren: false,
                                            expandKey: 'total',
                                            isTotal: true
                                        }
                                    ];
                                })()
                            }
                            columns={[
                                {
                                    field: 'category',
                                    headerName: 'Category',
                                    flex: 1,
                                    minWidth: 200,
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                pl: params.row.isTotal ? 0 : params.row.level * 4,
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                width: '100%',
                                                borderTop: params.row.isTotal ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : 0
                                            }}
                                        >
                                            {params.row.hasChildren && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleCategory(params.row.expandKey)}
                                                    sx={{ mr: 1, p: 0 }}
                                                >
                                                    {expandedCategories.has(params.row.expandKey) ? (
                                                        <ExpandMore fontSize="small" />
                                                    ) : (
                                                        <ChevronRight fontSize="small" />
                                                    )}
                                                </IconButton>
                                            )}
                                            {params.value}
                                            {params.row.expandKey === 'ar_collected' && (
                                                <Tooltip
                                                    title="Prior transactions that were collected during this report period."
                                                    arrow
                                                    placement="right"
                                                >
                                                    <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                </Tooltip>
                                            )}
                                            {params.row.expandKey === 'cancelled_transactions' && (
                                                <Tooltip
                                                    title="Prior transactions cancelled during this report period. "
                                                    arrow
                                                    placement="right"
                                                >
                                                    <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )
                                },
                                {
                                    field: 'credit',
                                    headerName: 'Credit',
                                    flex: 0.7,
                                    minWidth: 120,
                                    align: 'right',
                                    headerAlign: 'right',
                                    renderHeader: () => (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                            <Tooltip title="Total amount collected successfully for transactions during the selected report period (excludes pending/cancelled transactions)" arrow placement="top">
                                                <InfoOutlined sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                            <strong>Credit</strong>
                                        </Box>
                                    ),
                                    valueFormatter: (params) => formatCurrency(params as number),
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                borderTop: params.row.isTotal ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : 0
                                            }}
                                        >
                                            {formatCurrency(params.value)}
                                        </Box>
                                    )
                                },
                                {
                                    field: 'debit',
                                    headerName: 'Debit',
                                    flex: 0.7,
                                    minWidth: 120,
                                    align: 'right',
                                    headerAlign: 'right',
                                    renderHeader: () => (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                            <Tooltip title="Total refunds issued during the selected report period for transactions within the same period." arrow placement="top">
                                                <InfoOutlined sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                            <strong>Debit</strong>
                                        </Box>
                                    ),
                                    valueFormatter: (params) => formatCurrency(params as number),
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                color: params.value > 0 ? 'error.main' : 'inherit',
                                                borderTop: params.row.isTotal ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : 0
                                            }}
                                        >
                                            {formatCurrency(params.value)}
                                        </Box>
                                    )
                                },
                                {
                                    field: 'tax',
                                    headerName: 'Tax',
                                    flex: 0.7,
                                    minWidth: 120,
                                    align: 'right',
                                    headerAlign: 'right',
                                    valueFormatter: (params) => formatCurrency(params as number),
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                borderTop: params.row.isTotal ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : 0
                                            }}
                                        >
                                            {formatCurrency(params.value)}
                                        </Box>
                                    )
                                },
                                {
                                    field: 'outstanding',
                                    headerName: 'Outstanding',
                                    flex: 0.7,
                                    minWidth: 120,
                                    align: 'right',
                                    headerAlign: 'right',
                                    renderHeader: () => (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                            <Tooltip title="Cumulative unpaid amount — all transactions with a transaction date on or before the report end date that still have a status of Pending, Failed, or In Progress." arrow placement="top">
                                                <InfoOutlined sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                            <strong>Outstanding</strong>
                                        </Box>
                                    ),
                                    valueFormatter: (params) => formatCurrency(params as number),
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                color: params.value > 0 ? 'warning.main' : 'inherit',
                                                borderTop: params.row.isTotal ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : 0
                                            }}
                                        >
                                            {formatCurrency(params.value)}
                                        </Box>
                                    )
                                },
                                {
                                    field: 'net',
                                    headerName: 'Net',
                                    flex: 0.7,
                                    minWidth: 120,
                                    align: 'right',
                                    headerAlign: 'right',
                                    renderHeader: () => (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                            <Tooltip title="Net revenue collected: Credit minus Debit (refunds), plus any Accounts Receivable collected from prior periods. Represents actual realized revenue for this period." arrow placement="top">
                                                <InfoOutlined sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                            <strong>Net</strong>
                                        </Box>
                                    ),
                                    valueFormatter: (params) => formatCurrency(params as number),
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                borderTop: params.row.isTotal ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : 0
                                            }}
                                        >
                                            {formatCurrency(params.value)}
                                        </Box>
                                    )
                                }
                            ] as GridColDef[]}
                            autoHeight
                            hideFooter
                            disableRowSelectionOnClick
                            disableColumnMenu
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid rgba(224, 224, 224, 1)'
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: 'background.default',
                                    borderBottom: '2px solid rgba(224, 224, 224, 1)'
                                }
                            }}
                        />
                    ) : (
                        <Typography color="textSecondary" variant="body2" textAlign="center">
                            No revenue breakdown available
                        </Typography>
                    )}
                </Paper>
            </Box>
        </LocalizationProvider>
        </Container>
    );
};
